import { useState, useEffect, useContext } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import logo from "../global/logo.png";
import axiosClient from "../../axios";
import UserContext from "../../contexts/UserContext";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportIcon from "@mui/icons-material/Report";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";

const NAV_ITEMS = [
    { type: "active", label: "Active", icon: <PlayCircleOutlineIcon />, color: "#4cceac" },
    { type: "overdue", label: "Overdue", icon: <WarningAmberIcon />, color: "#db4f4a" },
    { type: "escalated", label: "Escalated", icon: <ReportIcon />, color: "#FF9800" },
    { type: "submitted", label: "Submitted", icon: <PendingActionsIcon />, color: "#00BCD4" },
    { type: "completed", label: "Completed", icon: <CheckCircleOutlineIcon />, color: "#2e7c67" },
    { type: "new", label: "New Surveys", icon: <FiberNewOutlinedIcon />, color: "#6870fa" },
    { type: "archived", label: "Archived", icon: <ArchiveOutlinedIcon />, color: "#858585" },
];

const BrokerSidebar = ({ getIsLoginFlag }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { token, user, deleteToken } = useContext(UserContext);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token || !user) {
            getIsLoginFlag(true);
            navigate("/login");
        }
    }, [token, user]);

    const handleLogout = async () => {
        await axiosClient.post("/user/logout", {}).catch(() => {});
        deleteToken();
        getIsLoginFlag(true);
        navigate("/login");
    };

    return (
        <Box
            sx={{
                "& .pro-sidebar-inner": {
                    background: `${colors.primary[400]} !important`,
                },
                "& .pro-icon-wrapper": {
                    backgroundColor: "transparent !important",
                },
                "& .pro-inner-item": {
                    padding: "5px 35px 5px 20px !important",
                },
                "& .pro-inner-item:hover": {
                    color: "#868dfb !important",
                },
                "& .pro-menu-item.active": {
                    color: "#6870fa !important",
                },
            }}
        >
            <ProSidebar collapsed={isCollapsed}>
                <Menu iconShape="square">
                    {/* Logo + collapse toggle */}
                    <MenuItem
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
                        style={{
                            margin: "10px 0 20px 0",
                            color: colors.grey[100],
                        }}
                    >
                        {!isCollapsed && (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                ml="15px"
                            >
                                <img
                                    alt="OCCUSURV"
                                    width="150px"
                                    height="30px"
                                    src={logo}
                                    style={{ cursor: "pointer" }}
                                />
                                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                                    <MenuOutlinedIcon />
                                </IconButton>
                            </Box>
                        )}
                    </MenuItem>

                    {/* User profile block */}
                    {!isCollapsed && (
                        <Box mb="20px" textAlign="center">
                            <PersonOutlinedIcon
                                sx={{ fontSize: "52px", color: colors.greenAccent[400] }}
                            />
                            <Typography
                                variant="h5"
                                color={colors.grey[100]}
                                fontWeight="bold"
                            >
                                {user?.name || "Broker"}
                            </Typography>
                            <Typography variant="body2" color={colors.greenAccent[400]}>
                                Broker Portal
                            </Typography>
                        </Box>
                    )}

                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        {/* Section label */}
                        {!isCollapsed && (
                            <Typography
                                variant="h6"
                                color={colors.grey[300]}
                                sx={{ m: "15px 0 5px 20px" }}
                            >
                                My Surveys
                            </Typography>
                        )}

                        {NAV_ITEMS.map(({ type, label, icon, color }) => {
                            const isActive = pathname === `/broker/${type}`;
                            return (
                                <MenuItem
                                    key={type}
                                    active={isActive}
                                    style={{
                                        color: isActive ? color : colors.grey[100],
                                        borderLeft: isActive
                                            ? `3px solid ${color}`
                                            : "3px solid transparent",
                                    }}
                                    icon={
                                        <span style={{ color: isActive ? color : colors.grey[300] }}>
                                            {icon}
                                        </span>
                                    }
                                >
                                    <Typography>{label}</Typography>
                                    <Link to={`/broker/${type}`} />
                                </MenuItem>
                            );
                        })}

                        {/* Account section */}
                                                {/* Reports section */}
                                                {!isCollapsed && (
                                                    <Typography
                                                        variant="h6"
                                                        color={colors.grey[300]}
                                                        sx={{ m: "20px 0 5px 20px" }}
                                                    >
                                                        Analytics
                                                    </Typography>
                                                )}
                                                <MenuItem
                                                    active={pathname === "/broker/reports"}
                                                    style={{
                                                        color: pathname === "/broker/reports" ? "#a4a9fc" : colors.grey[100],
                                                        borderLeft:
                                                            pathname === "/broker/reports"
                                                                ? "3px solid #a4a9fc"
                                                                : "3px solid transparent",
                                                    }}
                                                    icon={
                                                        <span
                                                            style={{
                                                                color:
                                                                    pathname === "/broker/reports"
                                                                        ? "#a4a9fc"
                                                                        : colors.grey[300],
                                                            }}
                                                        >
                                                            <BarChartOutlinedIcon />
                                                        </span>
                                                    }
                                                >
                                                    <Typography>Reports</Typography>
                                                    <Link to="/broker/reports" />
                                                </MenuItem>

                                                {/* Account section */}
                        {!isCollapsed && (
                            <Typography
                                variant="h6"
                                color={colors.grey[300]}
                                sx={{ m: "20px 0 5px 20px" }}
                            >
                                Account
                            </Typography>
                        )}

                        <MenuItem
                            style={{ color: colors.grey[100] }}
                            icon={<LogoutIcon sx={{ color: colors.redAccent[400] }} />}
                            onClick={handleLogout}
                        >
                            <Typography>Logout</Typography>
                        </MenuItem>
                    </Box>
                </Menu>
            </ProSidebar>
        </Box>
    );
};

export default BrokerSidebar;
